import { MenuItem, TreeNode } from 'primeng/api';
import { Observable } from 'rxjs';
import { filter, finalize, share, switchMap, takeUntil, tap } from 'rxjs/operators';

import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output, Self, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { SpinnerService } from '../../components/spinner/spinner.service';
import { DialogCloseReason, IDialogOnClose } from '../../dialogs/base-dialog';
import { DialogService } from '../../dialogs/dialog.service';
import { FileDeleteAlertComponent } from '../../dialogs/file-delete-alert/file-delete-alert.component';
import { RPC } from '../../frame-rpc';
import { IFileBase } from '../../interfaces/files.interface';
import { TreeViewService } from '../../typescript-angular-client/api/treeView.service';
import { FolderTreeNode } from '../../typescript-angular-client/model/folderTreeNode';
import { DragAndDropEvent, FileModel } from '../../typescript-angular-client/model/models';
import { FmUploaderService } from '../file-uploader/fm-uploader.service';
import { FilesListComponent } from '../files-list/files-list.component';
import { FilesListService, NewFilesListService } from '../files-list/files-list.service';
import { FmFoldersTreeComponent } from '../fm-folders-tree/fm-folders-tree.component';
import { ImageSyncService } from '../image-sync/image-sync.service';
import { ProgressBarData } from '../models/progress-bar-data.model';
import { FileManagerTypeEnum } from '../services/file-manager-api';
import { FmTreeService } from '../services/fm-tree.service';
import { ImageFileEditorService } from '../services/image-file-editor.service';
import { NgOnDestroyService } from '../services/ng-on-destroy.service';
import { IsSingleFileModel, IsTreeNode } from '../utils';
import { matchNode } from './match-node/match-node';

export interface IChangedFiles {
  deletedFiles: Array<IFileBase>;
  replacedFiles: Array<IFileBase>;
}

enum SearchMode {
  Folders,
  Library,
}

@Component({
  selector: 'file-manager-dialog-content',
  viewProviders: [],
  templateUrl: './file-manager-dialog-content.html',
  providers: [NgOnDestroyService],
  changeDetection: ChangeDetectionStrategy.Default
})
export class FileManagerDialogContentComponent implements OnInit, OnDestroy {
  @Output() selectedFileChanged = new EventEmitter<FileModel>();
  @Output() selectedFolderChanged = new EventEmitter<FolderTreeNode>();
  @Output() severalFilesSelected = new EventEmitter<boolean>();
  @Output() changedFilesUpdated = new EventEmitter<IChangedFiles>();
  @Output() progressBarDataEmit = new EventEmitter<ProgressBarData>();
  @Input() dialogType: FileModel.FileTypeEnum;
  @Input() maxFileSize = Math.pow(1024, 2) * 2;
  @Input() enableImageEditing = true;
  @Input() fileListPageSize?: number;
  @Input() previewFileNameMaxLength?: number;
  @Input() previewImgMaxHeight?: number;
  @Input() previewImgMaxWidth?: number;
  @Input() showUploaderBelowList?: boolean;
  @Input() showExportButton: boolean;

  @ViewChild('fL', { static: false }) fList: FilesListComponent;
  @ViewChild('fmTree', { static: false }) fTree: FmFoldersTreeComponent;

  SearchMode = SearchMode;

  readonly imageDialogType = FileModel.FileTypeEnum.IMAGES;
  public progressBarData: ProgressBarData = {
    width: 20,
    status: false,
    numberFiles: 0,
    currentFileIndex: 0,
  };
  private rpc: RPC;
  public FileManagerTypeEnum = FileManagerTypeEnum;
  public changedFiles: IChangedFiles = { deletedFiles: [], replacedFiles: [] };
  public currentFolder: string;
  public selectedFiles: FileModel[];
  public email: string;
  public menuItems: MenuItem[] = [
    {
      label: 'Rename',
      icon: 'pi pi-fw pi-pencil',
      command: () => this.onRenameItem(),
    },
    {
      label: 'Delete',
      icon: 'pi pi-fw pi-trash',
      command: () => this.onDeleteItem(),
    },
  ];

  constructor(
    public treeViewService: TreeViewService,
    public filesListService: FilesListService,
    private fmTreeService: FmTreeService,
    private flService: NewFilesListService,
    private imageFileEditorService: ImageFileEditorService,
    private spinnerService: SpinnerService,
    private dialogService: DialogService,
    private translate: TranslateService,
    private uploaderService: FmUploaderService,
    private imageSyncService: ImageSyncService,
    @Self() private ngOnDestroy$: NgOnDestroyService
  ) {
    if (this.fileListPageSize == null) {
      this.fileListPageSize = 16;
    }
    if (this.showUploaderBelowList == null) {
      this.showUploaderBelowList = false;
    }
  }

  ngOnInit(): void {
    this.uploaderService.completedAll$
      .pipe(takeUntil(this.ngOnDestroy$))
      .subscribe(() => this.refreshList());
    this.setUpCommunicationWithHostPage();
  }

  private setUpCommunicationWithHostPage(): void {
    this.rpc = new RPC(window, window.parent, '*', {});
    this.selectedFileChanged.subscribe((event) =>
      this.rpc.call('onSelectedFileChanged', event)
    );
  }

  ngOnDestroy(): void {
    this.flService.clear();
  }

  get folders(): Observable<FileModel[]> {
    return this.fmTreeService.fileLoaded;
  }

  get files(): Observable<FileModel[]> {
    return this.flService.files$;
  }

  get isFileOnlyMode(): Observable<boolean> {
    return this.flService.isFilesOnlyMode$;
  }

  get loadingFiles(): Observable<boolean> {
    return this.flService.isLoading$;
  }

  get isMultiSelected(): boolean {
    if (!this.selectedFiles) {
      return true;
    }
    return this.selectedFiles.length > 1;
  }

  get isFolderSelected(): boolean {
    if (!this.selectedFiles) {
      return true;
    }
    return this.isMultiSelected || this.selectedFiles.some((v) => v.IsFolder);
  }

  get currentFolderForUpload(): string {
    if (this.flService.isFilesOnlyMode) {
      return '0';
    } else {
      return this.fTree && this.fTree.SelectedNode
        ? this.fTree.SelectedNode.key
        : undefined;
    }
  }

  public searchModeSelectButtonOnChangeEvent(
    event: unknown,
    searchMode: SearchMode
  ): void {
    const treeNodeToPass =
      searchMode === SearchMode.Folders ? this.fTree.SelectedNode : null;
    // const treeNodeToPass = this.fTree.SelectedNode;
    this.flService.setFilesOnlyMode(
      searchMode === SearchMode.Library,
      treeNodeToPass,
      this.dialogType,
      this.fileListPageSize
    );
  }

  public sendProgressBarData(data: ProgressBarData): void {
    this.progressBarDataEmit.emit(data || this.progressBarData);
  }

  public folderSelected(folder: FolderTreeNode): void {
    this.selectedFiles = null;
    this.fList.selection = null;
    this.selectedFileChanged.emit(null);

    if (folder !== null) {
      this.flService.loadByFolder(
        folder,
        this.dialogType,
        this.fileListPageSize
      );
    }
  }

  public folderDragStarted(folder: FolderTreeNode): void {
    this.fTree.folderDrag = folder;
  }

  public onFileSelected(event): void {
    this.selectedFiles = event;
    if (!this.isMultiSelected) {
      const file = this.selectedFiles[0];
      if (file && file.IsFolder) {
        this.selectedFileChanged.emit(null);
        return;
      }
      this.selectedFileChanged.emit(file);
    } else {
      this.selectedFileChanged.emit(null);
    }
  }

  public onDeleteItem(): void {
    this.dialogService
      .createDialog(FileDeleteAlertComponent, {
        itemsCount: this.selectedFiles.length,
        isJustFolder:
          this.selectedFiles.length === 1 && this.selectedFiles[0].IsFolder,
        dialogType: this.dialogType,
      })
      .onClose.pipe(
        filter((r: IDialogOnClose) => r.reason === DialogCloseReason.OK),
        tap(() => this.spinnerService.show()),
        switchMap(() => this.flService.deleteFiles(this.selectedFiles))
      )
      .subscribe({
        next: (result: string[]) => {
          this.spinnerService.hide();
          this.fList.selection = null;
          this.fList.elements = this.fList.elements.filter(
            (x) => !result.some((r) => r === x.Id)
          );
          this.changedFiles.deletedFiles.splice(
            0,
            this.changedFiles.deletedFiles.length,
            ...result.map((s) => <IFileBase>{ id: s })
          );
          this.changedFilesUpdated.emit(this.changedFiles);
          this.fTree.RefreshCommand();
        },
        error: () => this.spinnerService.hide(),
      });
  }

  public onRenameItem(): void {
    const isFolder = this.selectedFiles[0].IsFolder;
    let fileName = '';

    const dialogHandle = this.dialogService.input({
      fieldDefaultValue: this.selectedFiles[0].Name,
      header: this.translate.instant(
        isFolder
          ? 'COMMON.DIALOGS.RENAME_FOLDER.RENAME_FOLDER'
          : 'COMMON.DIALOGS.RENAME_FILE.RENAME_FILE'
      ),
      fieldName: this.translate.instant(
        isFolder
          ? 'COMMON.DIALOGS.RENAME_FOLDER.FOLDER_NAME'
          : 'COMMON.DIALOGS.RENAME_FILE.FILE_NAME'
      ),
      okCaption: this.translate.instant(
        isFolder
          ? 'COMMON.DIALOGS.RENAME_FOLDER.RENAME'
          : 'COMMON.DIALOGS.RENAME_FILE.RENAME'
      ),
      cancelCaption: this.translate.instant(
        'COMMON.DIALOGS.RENAME_FOLDER.CANCEL'
      ),
      fieldValidationContext: { selectedFile: this.selectedFiles[0] },
      fieldValidationCallback: (v) => {
        fileName = v;
        return this.fileNameValidation(v);
      },
    });

    dialogHandle.onClose
      .pipe(
        filter((response) => response.reason === DialogCloseReason.OK),
        tap(() => this.spinnerService.show()),
        switchMap(() => {
          if (isFolder) {
            return this.treeViewService.treeViewUpdateFolder(
              {
                folderName: fileName,
                id: this.selectedFiles[0].Id,
                parentId: this.selectedFiles[0].ParentFolderId,
              },
              this.dialogType
            );
          } else {
            return this.flService.updateFile(
              {
                Name: fileName,
                Id: this.selectedFiles[0].Id,
                ParentFolderId: this.selectedFiles[0].ParentFolderId,
              },
              this.dialogType
            );
          }
        }),
        finalize(() => this.spinnerService.hide())
      )
      .subscribe(() => {
        this.selectedFiles[0].Name = fileName;
        if (isFolder) {
          this.fTree.RefreshCommand();
        }
        this.spinnerService.hide();
      });
  }

  public onSyncImages(): void {
    let email = 'Please enter an email address.';

    const dialogHandle = this.dialogService.input({
      header: this.translate.instant('COMMON.DIALOGS.SYNC_IMAGES.SYNC_IMAGES'),
      body: this.translate.instant('COMMON.DIALOGS.SYNC_IMAGES.DESCRIPTION'),
      fieldName: this.translate.instant(
        'COMMON.DIALOGS.SYNC_IMAGES.EMAIL_NOTIFICATION'
      ),
      fieldPlaceHolder: this.translate.instant(
        'COMMON.DIALOGS.SYNC_IMAGES.PLACEHOLDER'
      ),
      okCaption: this.translate.instant('COMMON.DIALOGS.SYNC_IMAGES.SYNC'),
      cancelCaption: this.translate.instant(
        'COMMON.DIALOGS.SYNC_IMAGES.CANCEL'
      ),
      fieldValidationContext: { email: this.email },
      fieldValidationCallback: (v) => {
        email = v;
        return this.emailValidation(v);
      },
    });

    dialogHandle.onClose
      .pipe(
        filter((r: IDialogOnClose) => r.reason === DialogCloseReason.OK),
        switchMap(() => this.imageSyncService.syncImages(email))
      )
      .subscribe();
  }

  private fileNameValidation(newName: string): string {
    const isFolder = this.selectedFiles[0].IsFolder;
    newName = newName.trim();

    if ((isFolder && newName.length < 3) || newName.length > 255) {
      return this.translate.instant(
        'COMMON.DIALOGS.CREATE_NEW_FOLDER.NAME_LENGTH_VALIDATION'
      );
    }

    if ((!isFolder && newName.length < 5) || newName.length > 255) {
      return this.translate.instant(
        'COMMON.DIALOGS.RENAME_FILE.NAME_LENGTH_VALIDATION'
      );
    }

    if (this.fList.elements.filter((x) => x.Name === newName).length > 0) {
      return this.translate.instant(
        'COMMON.DIALOGS.' +
          (isFolder
            ? 'RENAME_FOLDER.FOLDER_ALREADY_EXISTS_VALIDATION'
            : 'RENAME_FILE.FILE_ALREADY_EXISTS_VALIDATION')
      );
    }

    return null;
  }

  private emailValidation(email: string): string {
    if (!email) {
      return this.translate.instant(
        'COMMON.DIALOGS.SYNC_IMAGES.EMAIL_VALIDATION'
      );
    }

    return null;
  }

  public refreshList(): void {
    this.folderSelected(this.fTree.SelectedNode);
  }

  public fileListContextMenuSelected(): void {
    this.menuItems[0].disabled = this.isMultiSelected;
  }

  public startSearch(event: string): void {
    this.fList.selection = null;
    this.flService.search(
      event,
      this.fTree.SelectedNode,
      this.dialogType,
      this.fileListPageSize
    );
  }

  public fileDropped(event: any): void {
    if (!event.draggedItem) {
      return;
    }

    let droppedItem: FileModel;

    if (IsSingleFileModel(event.droppedItem)) {
      droppedItem = event.droppedItem as FileModel;
      if (!droppedItem || !droppedItem.IsFolder) {
        return;
      }
    }

    const dragged = IsTreeNode(event.draggedItem)
      ? [(event.draggedItem as TreeNode).data as FileModel]
      : this.selectedFiles || [event.draggedItem];

    if (
      dragged.some((value) => value.IsFolder && value.Id === droppedItem.Id)
    ) {
      if (event.isFolderTreeDrop) {
        this.fTree.tryGetAllFolders(this.fTree.SelectedNode, true);
      }
      return;
    }

    // trying to move parent into it's children will lead to cycle reference
    if (droppedItem.IsFolder) {
      const droppedParents = this.fTree.getFolderParents(droppedItem.Id);
      if (
        droppedParents.length > 0 &&
        dragged.some(
          (value) => value.IsFolder && droppedParents.indexOf(value.Id) > -1
        )
      ) {
        if (event.isFolderTreeDrop) {
          this.fTree.tryGetAllFolders(this.fTree.SelectedNode, true);
        }
        return;
      }
    }

    if (event.isFolderTreeDrop) {
      if (
        dragged.some(
          (draggedValue) =>
            draggedValue.IsFolder &&
            draggedValue.ParentFolderId === droppedItem.Id
        )
      ) {
        this.fTree.tryGetAllFolders(this.fTree.SelectedNode, true);
        return;
      }
    } else {
      if (
        dragged[0].Id === droppedItem.Id ||
        this.fList.elements.some(
          (value) =>
            dragged[0].ParentFolderId === droppedItem.Id ||
            (dragged[0].Id === value.Id &&
              !(
                droppedItem.IsFolder &&
                droppedItem.Id !== dragged[0].id &&
                droppedItem.Id !== dragged[0].ParentFolderId
              ))
        )
      ) {
        return;
      }
    }

    this.spinnerService.show();
    this.flService
      .moveFiles(droppedItem, dragged)
      .pipe(
        finalize(() => {
          this.fTree.tryGetAllFolders(this.fTree.SelectedNode, true);
          this.spinnerService.hide();
        })
      )
      .subscribe(() => {
        this.selectedFiles = null;
        this.fList.elements = [];
        this.spinnerService.hide();
      });
  }

  public folderFromThreeDropped(event: DragAndDropEvent): void {
    this.spinnerService.show();
    let droppedFolderId = this.fTree.SelectedNode.key;
    const droppedItem = event.droppedItem as FileModel;

    if (droppedItem) {
      droppedFolderId = droppedItem.Id;
    }

    this.treeViewService
      .treeViewUpdateFolder({
        id: this.fTree.folderDrag.key,
        parentId: droppedFolderId,
        folderName: this.fTree.folderDrag.label,
      })
      .subscribe(() => {
        this.fTree.RefreshCommand();
        this.spinnerService.hide();
      });
  }

  public handleFilesUploaded(): void {
    this.filesListService.searchTerm = '';
    this.filesListService.refresh(false, this.fileListPageSize);
  }

  public onNewFileAdded(): void {
    this.filesListService.searchTerm = '';
    this.filesListService.refresh(true, this.fileListPageSize);
  }

  public onEditImage(event: unknown): void {
    if (this.isMultiSelected || this.isFolderSelected) {
      return;
    }
    const selectedFile = this.selectedFiles[0];
    this.imageFileEditorService
      .editImage({
        contentType: selectedFile.ContentType,
        height: selectedFile.Height,
        id: selectedFile.Id,
        name: selectedFile.Name,
        parentFolderId: selectedFile.ParentFolderId,
        size: selectedFile.Size,
        src: selectedFile.Link,
        uploadDate: selectedFile.CreatedOn.toString(),
        width: selectedFile.Width,
      })
      .pipe(
        share(),
        finalize(() => this.spinnerService.hide())
      )
      .subscribe((result) => {
        const model: FileModel = {
          ContentType: result.contentType,
          Id: result.id,
          CreatedOn: new Date(result.uploadDate),
          IsFolder: false,
          Height: result.height,
          Width: result.width,
          Link: result.src,
          Size: result.size,
          Name: result.name,
          ParentFolderId: result.parentFolderId,
        };
        this.fList.elements.push(model);
        this.fList.selection = [model];
        this.spinnerService.hide();
        this.selectedFileChanged.emit(model);
      });
  }

  public selectFolder(event: FileModel): void {
    const node = matchNode(event.Id, this.fTree.SelectedNode);
    if (!node) {
      return;
    }
    (this.fTree.SelectedNode as TreeNode).expanded = true;
    this.fList.elements.length = 0;
    this.fTree.SelectedNode = node;
  }
}
